import { WSAlert, WSElement, WSList } from "@wingspanhq/fe-component-library";
import { WSListProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSList/WSList.component";
import React, { useMemo } from "react";
import { queriesInternalAccounts, queriesPayees } from "@query";
import { AnimationGroup } from "../../../../shared/components/AnimationGroup";
import { useLocationStateAlert } from "../../../../shared/hooks/useLocationStateAlert";
import { AlertPayoutMethodMissingData } from "../../../Settings/components/AlertPayoutMethodMissingData";
import { BannerBankingVerification } from "../../../Wallet/components/BannerBankingVerification";
import { selectorAlerts } from "../../selectors/selectorAlerts";
import { BannerT4ATaxForm } from "../BannerT4ATaxForm";
import { NotificationAlert } from "./NotificationAlert";
import { PendingInvitationsAlerts } from "./PendingInvitationsAlerts";
import { useTaxVerificationVersion } from "../../../../shared/hooks/useTaxVerificationVersion";
import { ConfirmTaxInformationForLegacyUserAlert } from "../../../TaxDocuments/components/ConfirmTaxInformationForLegacyUserAlert";
import { WSNotificationResponse } from "../Notifications";

type Props = {
  notifications: WSNotificationResponse[];
  showAlerts: boolean;
} & WSListProps;

export const AlertsList: React.FC<Props> = props => {
  const { notifications, showAlerts, ...listProps } = props;

  const queryPayees = queriesPayees.listPayees.useQuery({});
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const locationStateAlert = useLocationStateAlert();
  const { isLegacyOnlyTaxVerification } = useTaxVerificationVersion();

  const hasPendingAccount = useMemo(
    () =>
      (queryInternalAccounts.data?.data || []).some(
        internalAccount =>
          (internalAccount.type === "Banking" ||
            internalAccount.type === "TaxWithholding") &&
          internalAccount.status === "Pending"
      ),
    [queryInternalAccounts.data]
  );

  // v3 has no user account-type / payer-context flags; a payer is derived as an
  // account that has at least one payee.
  const isPayerView = (queryPayees.data?.data?.length ?? 0) > 0;

  const alerts = useMemo(
    () => selectorAlerts(props.notifications),
    [props.notifications]
  );

  return (
    <AnimationGroup<WSListProps>
      component={WSList}
      gap="S"
      startDelay={500}
      type="slideDown"
      timeout={1000}
      ready={showAlerts}
      {...listProps}
    >
      {isLegacyOnlyTaxVerification && (
        <ConfirmTaxInformationForLegacyUserAlert />
      )}

      {locationStateAlert && <WSAlert {...locationStateAlert} />}

      {!isPayerView && hasPendingAccount && <BannerBankingVerification />}

      <AlertPayoutMethodMissingData />

      <PendingInvitationsAlerts />

      <BannerT4ATaxForm />

      {alerts.map(notification => (
        <NotificationAlert key={notification.id} notification={notification} />
      ))}
      <WSElement key="spacer" mb="2XL" />
    </AnimationGroup>
  );
};
